import React, {useEffect, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Animated,
  Easing,
} from 'react-native';
import {useRemScale} from 'lib/remScale';

/**
 * Centered "Tutor is coming, please wait" dialog.
 *
 * Shown once the 3D scene is visible but the course content / tutor is still
 * loading (i.e. the top HUD lesson/rewards buttons are still skeletons and not
 * yet pressable). It disappears as soon as those buttons become pressable.
 *
 * Purely informational: it never captures touches (pointerEvents="none").
 */
export default function TutorLoadingDialog({visible, tutorName}) {
  const {rem} = useRemScale();
  const pulse = useRef(new Animated.Value(0.85)).current;

  useEffect(() => {
    if (!visible) {
      pulse.setValue(0.85);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0.85,
          duration: 800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => {
      try {
        loop.stop();
      } catch (_) {}
    };
  }, [visible, pulse]);

  if (!visible) return null;

  return (
    <View
      style={[styles.overlay, {paddingTop: rem(88)}]}
      pointerEvents="none">
      <Animated.View
        style={[
          styles.card,
          {
            minWidth: rem(220),
            maxWidth: rem(320),
            paddingVertical: rem(24),
            paddingHorizontal: rem(28),
            borderRadius: rem(18),
            opacity: pulse,
          },
        ]}>
        <ActivityIndicator size="large" color="#C5E836" />
        <Text style={[styles.title, {marginTop: rem(16), fontSize: rem(18)}]}>
          {`${tutorName || 'Tutor'} is coming`}
        </Text>
        <Text style={[styles.subtitle, {marginTop: rem(6), fontSize: rem(13)}]}>
          Please wait while your tutor arrives…
        </Text>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'flex-start',
    zIndex: 45,
  },
  card: {
    alignItems: 'center',
    backgroundColor: 'rgba(15,16,20,0.92)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.30)',
    shadowColor: '#000',
    shadowOpacity: 0.45,
    shadowRadius: 20,
    shadowOffset: {width: 0, height: 10},
  },
  title: {
    color: '#F6FBFF',
    fontWeight: '700',
    letterSpacing: 0.3,
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(246,251,255,0.70)',
    fontWeight: '500',
    textAlign: 'center',
  },
});
